/**
 * DealsPage — the merged Deals surface (T12.1.5.1.3, Iteration 2).
 *
 * Consolidates the two standalone surfaces — `OffersPage` (per-store
 * `useRetailerOffers`, gated on `useSelectedStores`, with the T26.1
 * `syncTriggered` cold-start poll) and `FlyerDealsPage` (`useFlyerDeals`,
 * grouped by store, ~55s cold-start poll) — into the one `/deals` tab the
 * Planning nav points at (parent decision C). `/offers` + `/flyerdeals`
 * redirect here.
 *
 * Iteration 2 redesigns the filter UX + layout to the reworked prototype
 * `#s-deals` (NOT the offer/flyer merge, which is unchanged):
 *   1. Server-ranked search + personalized complete-query typeahead.
 *   2. Permanent Filters + sort row, identical in browse and search — no
 *      scope segment (T55.13 retired it); "For you" (default sort) ranks
 *      personal relevance first, and browse always renders every eligible
 *      deal.
 *   3. One tabbed drawer for Category, Attribute, and Store filter sections,
 *      plus removable active filters and Clear all.
 *   4. Contextual result heading.
 *   5. Restyled deal cards with an inline + Add to list / ✓ On list action
 *      (D5) and the funded "flyer" signal kept in the sub-line (decision C).
 *   6. Tap a card → bottom-sheet detail (D6) with a cross-store "cheapest
 *      first" comparison + add / remove.
 *
 * Both data sources land as a single flat list of **deal cards**:
 *   - flyer deals carry a sale price + struck regular price + an end date and a
 *     funded-deal (flyer) marker; their offer anchor is `matched_retaileroffer_id`
 *     (may be null → the Add button disables, D5).
 *   - store offers carry a regular price only (catalog rows hold no sale price
 *     post-split T12.0.1.6); their offer anchor is the offer id. One with a live
 *     `has_active_deal` flags the same funded signal.
 *
 * Sort is an honest relevance stub (on-list > your-brand > on-sale tiebreak)
 * until T12.5 (TD#4) — a visible ordering, no hidden algorithm.
 *
 * Hooks can't loop, so store offers across every selected store are gathered
 * via one `StoreLocationOffersLoader` child per store that reports its offers (and its
 * `syncTriggered` cold-start flag) up through a callback. Flyer deals already
 * arrive grouped across all stores from `useFlyerDeals`.
 *
 * One responsive surface — reflows from the same components phone→desktop, no
 * bespoke desktop layout (parent § Responsive scope). Tokens-only (CP5): no
 * hex, no raw px; ≥44px targets; safe-area handled by the shell.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type Ref,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useSelectedStores,
  useStores,
  useRetailerOffers,
  useFlyerDeals,
  useActiveShoppingList,
  useHouseholdProducts,
  useAddToActiveList,
  useDeleteShoppingListItem,
  useDealsSearch,
  useDealsSearchSuggestions,
  useApi,
  useCurrentHousehold,
} from '@shopista/shared/api/hooks';
import type {
  AddShoppingListItemRequest,
  FlyerDealsStoreGroup,
  FlyerDealLine,
  RetailerOfferWithDetails,
  ShoppingListItemWithDetails,
  DealsSearchRow,
  DealsSearchFilterOption,
  DealCounterRow,
} from '@shopista/shared/types';
// T55.12 — the one shared counting module every Deals number reads from
// (heading, "Show N deals", tab badges, Category option rows). Pure/
// platform-free (packages/shared/src/types/dealCounters.ts).
import { computeDealCounters } from '@shopista/shared/types';
import { Card, Modal, Spinner } from '../../components';
import {
  deriveBadges,
  norm,
  systemProductMatch,
  brandMatch,
  type DealBadge,
} from './badges';
import { useBadgeSignals } from './useBadgeSignals';
import { useIncrementalList } from './useIncrementalList';
import { DealsSearch } from './DealsSearch';
import {
  ActiveFilterChips,
  DealsFiltersDrawer,
  type AttributeFilterIntent,
  type CategoryFilterIntent,
} from './DealsFiltersDrawer';
import { usePreferenceBroadcastSync } from '../../lib/preferenceBroadcast';
import { useLocationContext } from '../../stores/LocationContext';
// T12.6.1 — read the SAME location outcome AppShell's sole `useModeDetection`
// instance publishes, without calling that hook again (its single-caller
// invariant, T12.6 D5, forbids a second `getCurrentPosition` / OS prompt).
// `coordsOf` reuses the exact numeric parse the hook's own `hasHomeCoords` gate
// uses, so the household-coords check can't drift between the two surfaces.
import { coordsOf, useLocationSignal } from '../../stores/useModeDetection';
import { dismissSurface, isSurfaceDismissed } from '../../stores/dismissal';
// T27.2.7.9.4 — the deal-card model + the ONE deal pop-up moved into DealOverlay
// so the shopping-list rows mount the exact same sheet in place (no fork). The
// Deals page renders the same `DealDetailSheet` it always did, now imported.
import {
  DealDetailSheet,
  flyerDealToCard,
  offerToCard,
  searchRowToCard,
  formatPrice,
  priceBasisSuffix,
  formatEndDate,
  badgeStyleFor,
  saleBadgeStyle,
  type DealCard,
} from './DealOverlay';

// ---------------------------------------------------------------------------
// Cold-start cadence — mirrors the two retired pages so there is no regression
// in how long the merged surface waits for a freshly-linked store / fresh-FSA
// ingestion before falling back to the honest "nothing yet" copy.
// ---------------------------------------------------------------------------
const FLYER_COLD_START_POLL_MS = 5_000;
const FLYER_COLD_START_MAX_POLLS = 11; // ~55s, matches the old FlyerDealsPage.

// T27.2.7.9.4 — the `DealCard` model + its mappers + the deal sheet now live in
// ./DealOverlay (imported above) so the list and the Deals page share one model
// and one pop-up. The card-row + page styles below stay local.

// ---------------------------------------------------------------------------
// Styles — tokens only.
// ---------------------------------------------------------------------------
const TOUCH = '2.75rem'; // ≥44px touch floor (Apple HIG / Material).
const HAIRLINE = '0.0625rem';
// The Deals fine-tune row (Filters · sort) is a user-directed exception to
// the `TOUCH` floor above: the proto's own compact controls (~36px) read as
// noticeably shorter than this app's usual 44px controls, and the user
// explicitly chose that compactness for this ONE row over the
// frontend-standards touch-target floor. Do not reuse `COMPACT` outside this
// row without the same explicit direction.
const COMPACT = '2.25rem'; // 36px — proto `.filterbtn`/select height.

const pageStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
};

// The page's primary rhythm unit — every top-level block carries this bottom
// margin (search → control → chips → panel → heading → results). §8.
const blockGap = 'var(--space-3)';

// ---- Sticky cluster (T40) --------------------------------------------------
// The search field + the filter bar pin together below the app header as one
// cluster, so filtering stays reachable while browsing a long list. The
// window/body is the scroll container (AppShell `<main>` has no overflow), so a
// plain `position: sticky` pins against the viewport; `top` offsets by the live
// header height (`--app-header-height`, published by AppShell from the sticky
// header's real height incl. the native status-bar inset) so the cluster pins
// JUST BELOW the header rather than sliding under it — same pattern as the
// Feedback "Propose" banner. `--z-banner` lifts it above scrolling deal cards
// but below the app header (`--z-sticky`); the solid `--color-bg-primary` fill
// keeps cards from bleeding through as they scroll under it.
//
// Zero-drift pin: `<main>` (AppShell) carries its own `paddingTop:
// var(--space-4)`, so the cluster's NATURAL (in-flow) top sits `--space-4`
// below `top`'s threshold — without correction it would slide up that
// `--space-4` before locking (a visible ~16px creep). `marginTop:
// calc(-1 * var(--space-4))` cancels exactly that gap, so the natural top
// EQUALS the `top` threshold and the cluster is already "pinned" at scroll
// position zero. `paddingTop: var(--space-4)` re-adds the same amount INSIDE
// the box (pushing the visible search bar back down by `--space-4`, so its
// resting position is unchanged) — the opaque backplate now also covers that
// reclaimed strip, so nothing bleeds through it. `top` itself stays the plain
// header height (never raised above it) — raising `top` is what caused the
// header-overlap regression previously; do not reintroduce that.
const stickyClusterStyle: CSSProperties = {
  position: 'sticky',
  top: 'var(--app-header-height, 0px)',
  zIndex: 'var(--z-banner)' as unknown as number,
  display: 'flex',
  flexDirection: 'column',
  gap: blockGap,
  marginTop: 'calc(-1 * var(--space-4))',
  marginBottom: blockGap,
  paddingTop: 'var(--space-4)',
  paddingBottom: 'var(--space-2)',
  backgroundColor: 'var(--color-bg-primary)',
};

// ---- 1. Search bar ---------------------------------------------------------
const searchBarStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  borderWidth: HAIRLINE,
  borderStyle: 'solid',
  borderColor: 'var(--color-border-strong)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--space-2) var(--space-3)',
  backgroundColor: 'var(--color-bg-primary)',
  position: 'relative',
};

const searchBarFocusedStyle: CSSProperties = {
  ...searchBarStyle,
  borderColor: 'var(--color-border-focus)',
};

const searchIconStyle: CSSProperties = {
  flex: '0 0 auto',
  color: 'var(--color-text-muted)',
};

const searchInputStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontFamily: 'var(--font-family-sans)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-primary)',
};

const searchClearStyle: CSSProperties = {
  flex: '0 0 auto',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: TOUCH,
  minHeight: TOUCH,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  color: 'var(--color-text-muted)',
  fontSize: 'var(--font-size-base15)',
};

// ---- 2. Filter bar (Filters · sort) ----------------------------------------
// T40, restyled to the reworked prototype's `.dealfilters` row (`#s-deals`,
// docs/boards/dev/prototypes/shopista-app/screens/app-planning.html): ONE
// line at every width — Filters and the (now content-width, see
// `sortSelectStyle`) sort control must never wrap to a second row (T55.13
// retired the scope segment that used to share this row). `nowrap` is safe
// now that the sort control is compact rather than `flex:1`/`width:100%` —
// the full-width select, not this row, was what forced the wrap in the first
// place.
const filterBarStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  gap: 'var(--space-1)',
};

// Proto `.filterbtn`: ~36px (`COMPACT`), tightened horizontally (`--space-2`
// not `--space-3`) so the row's two controls fit one line at ~360px without
// wrapping or horizontal scroll. `boxSizing:'border-box'` so `minHeight`
// bounds the TOTAL rendered height (border + padding included), not just the
// content box.
// Longhand border/background (not the `border`/`background` shorthand) so
// `filterButtonActiveStyle` below can override just `borderColor`/
// `backgroundColor` without React's shorthand-vs-longhand rerender warning.
const filterButtonStyle: CSSProperties = {
  boxSizing: 'border-box',
  minHeight: COMPACT, padding: '0 var(--space-2)',
  borderWidth: HAIRLINE, borderStyle: 'solid', borderColor: 'var(--color-border-strong)',
  borderRadius: 'var(--radius-pill)',
  backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-secondary)',
  fontSize: 'var(--font-size-2sm)', fontWeight: 'var(--font-weight-semibold)',
  whiteSpace: 'nowrap', cursor: 'pointer',
};

// T55.12 — the Filters button drops its numeral (the drawer's tab badges +
// the chips row already carry that detail) and instead fills like an
// active pill — same accent-subtle/accent-primary token pair the drawer's
// own active tab + chip styling already uses — whenever any filter (a
// Category, an Attribute, or a Store) is applied.
const filterButtonActiveStyle: CSSProperties = {
  ...filterButtonStyle,
  borderColor: 'var(--color-accent-primary)',
  backgroundColor: 'var(--color-accent-subtle)',
  color: 'var(--color-accent-primary)',
};

// Proto `.deals-unified-controls .sortwrap`/`select`: pill, border-strong,
// 12.5px/600, solid `--bg`, ~36-38px (`COMPACT`). A plain native `<select>`
// (not a custom popover button) per the proto — simpler, and its own
// dropdown never contributes to page overflow. `flex: '0 0 auto'` — sizes to
// its own content instead of stretching to fill the row; `marginLeft: 'auto'`
// then pushes it to the row's right edge (Filters pill stays left-packed,
// parent `filterBarStyle`'s `nowrap` keeps this single-line regardless).
// `width: '10.5rem'` (168px, FIXED) — an earlier `width: 'auto'` +
// `maxWidth` pairing tracked the currently selected option's own text width,
// so the pill visibly resized as the choice changed; a fixed width makes
// every selection render identically, and no `minWidth` is needed since the
// fixed value already clears the touch floor on its own. Sized to the
// longest locked label ("Price: low to high") so it renders in full with
// zero truncation in the closed state; `whiteSpace: 'nowrap'` / `overflow:
// 'hidden'` / `textOverflow: 'ellipsis'` stay only as a defensive floor for
// any future label that outgrows the box. No inline `outline` so the global
// `:focus-visible` ring in responsive.css still applies (see
// reference_focus_ring_focus_visible_pattern).
const sortSelectStyle: CSSProperties = {
  boxSizing: 'border-box',
  flex: '0 0 auto',
  marginLeft: 'auto',
  width: '10.5rem',
  minHeight: COMPACT,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  // Tightened gutters (built from existing tokens, not a new value) so the
  // control stays compact enough for the one-line row at ~360px. The native
  // control's own reserved arrow space dominates its width regardless of
  // author padding, so this is deliberately snug on both sides.
  padding: '0 calc(var(--space-2) + var(--space-1)) 0 var(--space-1)',
  border: `${HAIRLINE} solid var(--color-border-strong)`, borderRadius: 'var(--radius-pill)',
  background: 'var(--color-bg-primary)', color: 'var(--color-text-secondary)',
  fontFamily: 'var(--font-family-sans)', fontSize: 'var(--font-size-2sm)',
  fontWeight: 'var(--font-weight-semibold)', cursor: 'pointer',
};
const typeaheadStyle: CSSProperties = {
  position: 'absolute', top: '100%', left: 0, right: 0,
  zIndex: 'var(--z-index-dropdown)' as unknown as number,
  display: 'flex', flexDirection: 'column', padding: 'var(--space-2)',
  border: `${HAIRLINE} solid var(--color-border-strong)`, borderRadius: 'var(--radius-lg)',
  background: 'var(--color-bg-primary)', boxShadow: 'var(--shadow-md)',
};
const typeaheadRowStyle: CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  minHeight: TOUCH, padding: '0 var(--space-3)', border: 'none', borderRadius: 'var(--radius-md)',
  background: 'transparent', color: 'var(--color-text-secondary)', cursor: 'pointer', textAlign: 'left',
};
const typeaheadSourceStyle: CSSProperties = { color: 'var(--color-text-muted)', fontSize: 'var(--font-size-3xs)' };
const lifecycleStyle: CSSProperties = { color: 'var(--color-text-muted)', fontSize: 'var(--font-size-3xs)' };

// ---- 3. Result heading -----------------------------------------------------
const headingStyle: CSSProperties = {
  marginTop: 'var(--space-1)',
  marginBottom: blockGap,
  fontSize: 'var(--font-size-2sm)',
  color: 'var(--color-text-muted)',
};

const headingEmphStyle: CSSProperties = {
  color: 'var(--color-text-secondary)',
  fontWeight: 'var(--font-weight-semibold)',
};

// ---- 6. Deal card ----------------------------------------------------------
const dealCardStyle: CSSProperties = {
  display: 'flex',
  gap: 'var(--space-3)',
  backgroundColor: 'var(--color-bg-primary)',
  borderWidth: HAIRLINE,
  borderStyle: 'solid',
  borderColor: 'var(--color-border-subtle)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-3)',
  marginBottom: 'var(--space-2)',
  cursor: 'pointer',
  textAlign: 'left',
  width: '100%',
  // BUG B (horizontal scroll) — no app-wide `box-sizing: border-box` reset
  // exists, so this defaulted to `content-box`: `width: 100%` sized the
  // CONTENT box to the full parent width, then the border + padding above
  // added on top, overflowing the card past its container (and, since the
  // overflow exceeded `<main>`'s own side padding, past the page itself —
  // the horizontal scrollbar). `border-box` folds border + padding inside
  // the 100%, so the card never exceeds its container.
  boxSizing: 'border-box',
  alignItems: 'stretch',
};

const dealImageStyle: CSSProperties = {
  flex: '0 0 auto',
  width: 'var(--space-12)',
  height: 'var(--space-12)',
  display: 'grid',
  placeItems: 'center',
  borderRadius: 'var(--radius-md)',
  backgroundColor: 'var(--color-bg-muted)',
  fontSize: 'var(--font-size-lg)',
  objectFit: 'cover',
};

const dealInfoStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
};

const dealNameStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-primary)',
};

// saleBadgeStyle moved to ./DealOverlay (imported) — shared by the card row + sheet.

const dealSubStyle: CSSProperties = {
  fontSize: 'var(--font-size-3xs)',
  color: 'var(--color-text-muted)',
  marginTop: 'var(--space-1)',
};

// T12.5.1 (C) — the stacked badge set wraps so several badges flow onto the card
// without forcing a fixed height. Designer owns truncation / "+N" overflow; the
// relative order is fixed by `BADGE_ORDER`.
const badgeRowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--space-1)',
  marginTop: 'var(--space-1)',
};

// Tone → colour. Positive = accent (the existing tag look); substitute = a muted
// neutral; negative = a danger-subtle (still informative, never alarming —
// avoided/excluded items are downranked, never hidden). Working palette; the
// designer pass sets the final per-badge colour.
// badgeStyleFor + its tone styles moved to ./DealOverlay (imported above).

const dealActStyle: CSSProperties = {
  flex: '0 0 auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  gap: 'var(--space-2)',
};

const priceColStyle: CSSProperties = {
  textAlign: 'right',
};

const nowSaleStyle: CSSProperties = {
  fontSize: 'var(--font-size-base)',
  fontWeight: 'var(--font-weight-bold)',
  color: 'var(--color-accent-tertiary)', // R1: pink = a price that dropped.
};

const nowRegularStyle: CSSProperties = {
  fontSize: 'var(--font-size-base)',
  fontWeight: 'var(--font-weight-bold)',
  color: 'var(--color-text-primary)', // R1: regular = plain body color.
};

const wasStyle: CSSProperties = {
  fontSize: 'var(--font-size-2xs)',
  color: 'var(--color-text-muted)',
  textDecorationLine: 'line-through',
};

const addBtnBase: CSSProperties = {
  whiteSpace: 'nowrap',
  minHeight: TOUCH,
  padding: 'var(--space-2) var(--space-3)',
  borderRadius: 'var(--radius-pill)',
  borderWidth: HAIRLINE,
  borderStyle: 'solid',
  fontSize: 'var(--font-size-3xs)',
  fontWeight: 'var(--font-weight-bold)',
  cursor: 'pointer',
};

const addBtnRestingStyle: CSSProperties = {
  ...addBtnBase,
  borderColor: 'var(--color-accent-primary)',
  backgroundColor: 'var(--color-accent-subtle)',
  color: 'var(--color-accent-primary)',
};

const addBtnOnListStyle: CSSProperties = {
  ...addBtnBase,
  borderColor: 'var(--color-status-success)',
  backgroundColor: 'var(--color-status-success-subtle)',
  color: 'var(--color-status-success)',
};

const addBtnDisabledStyle: CSSProperties = {
  ...addBtnBase,
  borderColor: 'var(--color-border-subtle)',
  backgroundColor: 'var(--color-bg-muted)',
  color: 'var(--color-text-muted)',
  cursor: 'not-allowed',
  opacity: 0.7,
};

const sheetActionsStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-3)',
  marginTop: 'var(--space-6)',
};

const sheetAddStyle: CSSProperties = {
  width: '100%',
  minHeight: TOUCH,
  padding: 'var(--space-3)',
  borderRadius: 'var(--radius-md)',
  border: 'none',
  backgroundColor: 'var(--color-accent-primary)',
  color: 'var(--color-text-inverse)',
  fontSize: 'var(--font-size-base15)',
  fontWeight: 'var(--font-weight-bold)',
  cursor: 'pointer',
};

const sheetAddDisabledStyle: CSSProperties = {
  ...sheetAddStyle,
  backgroundColor: 'var(--color-bg-muted)',
  color: 'var(--color-text-muted)',
  cursor: 'not-allowed',
  opacity: 0.7,
};

const sheetRemoveStyle: CSSProperties = {
  width: '100%',
  minHeight: TOUCH,
  padding: 'var(--space-3)',
  borderRadius: 'var(--radius-md)',
  borderWidth: HAIRLINE,
  borderStyle: 'solid',
  borderColor: 'var(--color-border-strong)',
  backgroundColor: 'var(--color-bg-primary)',
  color: 'var(--color-status-danger)',
  fontSize: 'var(--font-size-base15)',
  fontWeight: 'var(--font-weight-bold)',
  cursor: 'pointer',
};

// ---- Location ask (T12.6.1) -------------------------------------------------
// A dismissible inline row for a located-but-no-GPS household (home coords set,
// GPS itself not working): "Turn on location to auto-show deals for the store
// you're in." Dismissible with a 14-day re-surface (`stores/dismissal.ts`).
const LOCATION_ASK_SURFACE = 'deals-location-row';

const locationAskRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
};

const locationAskTextStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  fontSize: 'var(--font-size-2sm)',
  color: 'var(--color-text-secondary)',
  lineHeight: 1.35,
};

const locationAskActionsStyle: CSSProperties = {
  flex: '0 0 auto',
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
};

const locationAskEnableStyle: CSSProperties = {
  minHeight: TOUCH,
  border: 0,
  background: 'var(--color-accent-primary)',
  color: 'var(--color-text-inverse)',
  borderRadius: 'var(--radius-md)',
  padding: '0 var(--space-3)',
  fontSize: 'var(--font-size-3xs)',
  fontWeight: 'var(--font-weight-bold)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
  whiteSpace: 'nowrap',
};

const locationAskDismissStyle: CSSProperties = {
  minWidth: TOUCH,
  minHeight: TOUCH,
  border: 0,
  background: 'transparent',
  color: 'var(--color-text-muted)',
  fontSize: 'var(--font-size-sm)',
  lineHeight: 1,
  cursor: 'pointer',
  borderRadius: 'var(--radius-pill)',
  display: 'grid',
  placeItems: 'center',
};

// ---- Shared states ---------------------------------------------------------
const emptyStyle: CSSProperties = {
  padding: 'var(--space-6)',
  textAlign: 'center',
  color: 'var(--color-text-muted)',
};

const coldStartStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 'var(--space-3)',
  padding: 'var(--space-6)',
  textAlign: 'center',
  color: 'var(--color-text-muted)',
};

const errorStyle: CSSProperties = {
  color: 'var(--color-status-danger)',
  fontSize: 'var(--font-size-sm)',
};

// formatPrice / formatEndDate / packageLabel moved to ./DealOverlay (imported).

// ---------------------------------------------------------------------------
// Tag derivation (T12.5.1) — RELEVANCE + score only. The full stacked BADGE set
// lives in `deriveBadges` ([badges.ts](./badges.ts)); these tags drive the "For
// you" sort (on-list ∨ tracked, T55.13) and the visible relevance ordering:
//   - `onList`   — the card's title/brand loosely matches the active list
//     (relevance only; the BADGE is product-level, computed separately).
//   - `tracked`  — the card matches a tracked HouseholdProduct (loose token).
//   - `likedBrand` / `likedProduct` — a POSITIVE preference (like OR preferred)
//     matches, sharpening the order. Brand axis via `brandMatch` (exact curated
//     Brand); product axis via `systemProductMatch` (identity + flyer name
//     fallback).
export interface DealTags {
  onList: boolean;
  tracked: boolean;
  likedBrand: boolean;
  likedProduct: boolean;
}

export function deriveTags(
  card: {
    title: string;
    brand: string | null;
    systemProductId?: string | null;
    lines?: ReadonlyArray<{ system_product_id: string; name?: string }>;
  },
  listTokens: Set<string>,
  productTokens: Set<string>,
  likedBrandNames: ReadonlySet<string> = new Set(),
  likedSystemProductIds: ReadonlySet<string> = new Set(),
  likedProductNames: ReadonlySet<string> = new Set(),
): DealTags {
  const title = norm(card.title);
  const brand = norm(card.brand);
  const onList = tokenHit(title, brand, listTokens);
  const tracked = tokenHit(title, brand, productTokens);
  const likedBrand = brandMatch(card, likedBrandNames);
  const likedProduct = systemProductMatch(card, likedSystemProductIds, likedProductNames);
  return { onList, tracked, likedBrand, likedProduct };
}

function tokenHit(title: string, brand: string, tokens: Set<string>): boolean {
  for (const t of tokens) {
    if (!t) continue;
    if (title.includes(t) || (brand && brand.includes(t))) return true;
    if (title && t.includes(title)) return true;
  }
  return false;
}

// Honest relevance stub (TD#4) — on-list > liked-product > liked-brand >
// tracked > on-sale tiebreak. A visible ordering, no hidden algorithm; T12.5
// replaces it. Weights are spread so a higher signal always outranks any
// combination of lower ones (the lower three sum to 7 < 8).
function dealScore(tags: DealTags, card: DealCard): number {
  let s = 0;
  if (tags.onList) s += 16;
  if (tags.likedProduct) s += 8;
  if (tags.likedBrand) s += 4;
  if (tags.tracked) s += 2;
  if (card.salePrice != null) s += 1;
  return s;
}

// groupKey moved to ./DealOverlay (imported).

// ---------------------------------------------------------------------------
// StoreLocationOffersLoader — one per selected store. Hooks can't loop, so each store's
// `useRetailerOffers` lives in its own child that reports its offers + the
// T26.1 `syncTriggered` cold-start flag up. The parent merges them.
// ---------------------------------------------------------------------------
interface StoreLocationOffersReport {
  storeId: string;
  offers: RetailerOfferWithDetails[];
  isLoading: boolean;
  syncing: boolean;
}

function StoreLocationOffersLoader({
  storeId,
  onReport,
}: {
  storeId: string;
  onReport: (report: StoreLocationOffersReport) => void;
}): null {
  const query = useRetailerOffers(storeId);
  const offers = query.data?.offers;
  const syncing = query.data?.syncTriggered === true;
  const isLoading = query.isLoading;
  useEffect(() => {
    onReport({ storeId, offers: offers ?? [], isLoading, syncing });
  }, [storeId, offers, isLoading, syncing, onReport]);
  return null;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
type SortMode = 'foryou' | 'price' | 'price-desc' | 'ending';

// A plain native `<select>` per the proto (`.deals-unified-controls select`) —
// see `sortSelectStyle` above. `SORT_OPTIONS` renders its `<option>`s and gives
// the sticky-session-storage bootstrap (`initialSortMode`) its valid values.
// "For you" replaces the retired "Best match" default (T55.13): query fit
// stays the search ordering, personal relevance is now the browse order AND
// the search tiebreak (§ searchCardsWithTags / filteredSearchCards below).
const SORT_OPTIONS: ReadonlyArray<{ value: SortMode; label: string }> = [
  { value: 'foryou', label: 'For you' },
  { value: 'price', label: 'Price: low to high' },
  { value: 'price-desc', label: 'Price: high to low' },
  { value: 'ending', label: 'Ending soon' },
];
const SORT_STORAGE_KEY = 'shopista.deals.sort';

// T55.12/T55.17 — a shared empty Map for cards with no facet payload (offer
// cards carry no category/attribute tags), so `dealCounterRows` doesn't
// allocate a fresh empty Map per card per render. Shared by BOTH categories
// and attributes — `DealCounterRow.categories` is now weight-carrying
// (T55.17), the same shape as `.attributes`.
const EMPTY_TAG_MAP: ReadonlyMap<string, number> = new Map();

function initialSortMode(): SortMode {
  if (typeof window === 'undefined') return 'foryou';
  const saved = window.sessionStorage.getItem(SORT_STORAGE_KEY);
  return saved === 'price' || saved === 'price-desc' || saved === 'ending' ? saved : 'foryou';
}

export function DealsPage(): JSX.Element {
  const selectedStoresQuery = useSelectedStores();
  const storesQuery = useStores();
  const activeListQuery = useActiveShoppingList();
  const productsQuery = useHouseholdProducts();
  // T12.5.1 (G) — refresh badges when another tab changes a preference.
  usePreferenceBroadcastSync();
  // T39 — the session-only location signal. When the device is confidently in a
  // selected store, Deals defaults its store filter to that store ONCE per visit
  // (the effect below); it never reapplies after a user change/clear (AC7).
  const locationContext = useLocationContext();
  // T12.6.1 — the located-but-no-GPS inline ask. `useCurrentHousehold` is a
  // plain data read (already used elsewhere in the app); `useLocationSignal`
  // reads AppShell's published outcome without mounting `useModeDetection`
  // again.
  const householdQuery = useCurrentHousehold();
  const hasHomeCoords =
    coordsOf(householdQuery.data?.latitude, householdQuery.data?.longitude) !== null;
  const { locationWorking, requestDetection } = useLocationSignal();
  const [locationAskDismissed, setLocationAskDismissed] = useState(() =>
    isSurfaceDismissed(LOCATION_ASK_SURFACE),
  );
  const dismissLocationAsk = useCallback(() => {
    dismissSurface(LOCATION_ASK_SURFACE);
    setLocationAskDismissed(true);
  }, []);
  const showLocationAsk =
    locationWorking === false && hasHomeCoords && !locationAskDismissed;
  const addToList = useAddToActiveList();
  const deleteItem = useDeleteShoppingListItem();
  // T12.1.8.7 — reopen-the-added-deal entry point: `/deals?deal=<flyerdeal_id>`
  // opens the detail sheet for that deal once its card is loaded. A list item
  // that was flyer-added links here via its SLI `flyerdeal_id`.
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedStoreIds = useMemo(
    () => selectedStoresQuery.data?.storeIds ?? [],
    [selectedStoresQuery.data],
  );
  const storeNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const store of storesQuery.data?.stores ?? []) {
      map.set(store.id, store.name);
    }
    return map;
  }, [storesQuery.data?.stores]);

  // ---- Store offers across all selected stores (per-store loader children) --
  const [offerReports, setOfferReports] = useState<
    Record<string, StoreLocationOffersReport>
  >({});
  const handleReport = useCallback((report: StoreLocationOffersReport) => {
    setOfferReports((prev) => {
      const existing = prev[report.storeId];
      if (
        existing &&
        existing.offers === report.offers &&
        existing.isLoading === report.isLoading &&
        existing.syncing === report.syncing
      ) {
        return prev;
      }
      return { ...prev, [report.storeId]: report };
    });
  }, []);

  // Category and Attribute selections narrow the loaded browse/search payload
  // locally. Only query text changes issue the main search read. T55.17 —
  // Categories now cycle off → include → exclude too (`Set` → `Map`,
  // mirroring Attributes' own Set→Map history).
  const [selectedCategories, setSelectedCategories] = useState<Map<string, CategoryFilterIntent>>(() => new Map());
  const [selectedAttributes, setSelectedAttributes] = useState<Map<string, AttributeFilterIntent>>(() => new Map());
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sort, setSort] = useState<SortMode>(initialSortMode);
  useEffect(() => {
    window.sessionStorage.setItem(SORT_STORAGE_KEY, sort);
  }, [sort]);
  const [clearedFilters, setClearedFilters] = useState<string[]>([]);

  // ---- Flyer deals (all selected stores, grouped) with the bounded poll ------
  const [flyerPollCount, setFlyerPollCount] = useState(0);
  const flyerColdActive = flyerPollCount < FLYER_COLD_START_MAX_POLLS;
  const flyerQuery = useFlyerDeals(
    false,
    flyerColdActive ? FLYER_COLD_START_POLL_MS : false,
  );
  const flyerStores: FlyerDealsStoreGroup[] = useMemo(
    () => flyerQuery.data?.stores ?? [],
    [flyerQuery.data],
  );
  const hasAnyFlyerDeal = flyerStores.some((s) => s.deals.length > 0);

  const flyerUpdatedAt = flyerQuery.dataUpdatedAt;
  const lastFlyerCountedRef = useRef(0);
  useEffect(() => {
    if (flyerQuery.isLoading || flyerQuery.isError) return;
    if (hasAnyFlyerDeal) {
      setFlyerPollCount(0);
      lastFlyerCountedRef.current = 0;
      return;
    }
    if (flyerStores.length === 0) return; // no selected stores → no poll budget
    if (flyerUpdatedAt !== lastFlyerCountedRef.current) {
      lastFlyerCountedRef.current = flyerUpdatedAt;
      setFlyerPollCount((n) => n + 1);
    }
  }, [
    flyerUpdatedAt,
    hasAnyFlyerDeal,
    flyerStores.length,
    flyerQuery.isLoading,
    flyerQuery.isError,
  ]);

  // ---- Tag tokens -----------------------------------------------------------
  const listTokens = useMemo(() => {
    const set = new Set<string>();
    for (const item of activeListQuery.data?.items ?? []) {
      // The active list query is fetched with include_checked=true, so it carries
      // purchased/deferred items too. Only ACTIVE (pending) items count as "on list".
      if (item.status !== 'pending') continue;
      [item.product_name, item.display_title, item.offer_title, item.offer_brand]
        .map(norm)
        .filter(Boolean)
        .forEach((t) => set.add(t));
    }
    return set;
  }, [activeListQuery.data]);

  const productTokens = useMemo(() => {
    const set = new Set<string>();
    for (const product of productsQuery.data?.products ?? []) {
      [product.display_name, product.default_offer_title]
        .map(norm)
        .filter(Boolean)
        .forEach((t) => set.add(t));
    }
    return set;
  }, [productsQuery.data]);

  // T12.5.1 (C) — the full partitioned signal sets that drive the stacked badge
  // set. Built once via the shared hook (same logic the Planning list uses), then
  // threaded into `deriveBadges` per card. Empty sets simply never fire.
  const badgeSignals = useBadgeSignals();

  // T12.5.1 — the POSITIVE brand/product signals (like + preferred), used by
  // `deriveTags` for "For you" RELEVANCE + the score order. A brand/product the
  // household likes OR prefers makes a matching deal relevant; the negative and
  // substitute directions don't add relevance (they're caveats), so they stay
  // out of `deriveTags`.
  const likedBrandNames = useMemo(() => {
    const set = new Set(badgeSignals.likedBrandNames);
    for (const n of badgeSignals.preferredBrandNames) set.add(n);
    return set;
  }, [badgeSignals]);
  const likedSystemProductIds = useMemo(() => {
    const set = new Set(badgeSignals.likedProductIds);
    for (const id of badgeSignals.preferredProductIds) set.add(id);
    return set;
  }, [badgeSignals]);
  const likedProductNames = useMemo(() => {
    const set = new Set(badgeSignals.likedProductNames);
    for (const n of badgeSignals.preferredProductNames) set.add(n);
    return set;
  }, [badgeSignals]);

  // Map of the active list's offer anchors → the item, so the card / sheet can
  // show the ✓ state and Remove the right item.
  const activeListId = activeListQuery.data?.list?.id ?? null;
  const itemsByOfferId = useMemo(() => {
    const map = new Map<string, ShoppingListItemWithDetails>();
    for (const item of activeListQuery.data?.items ?? []) {
      // Only ACTIVE (pending) items count as "on list" — purchased/deferred items
      // are returned (include_checked=true) but must not flip a deal to "On list".
      if (item.status !== 'pending') continue;
      if (item.selected_retaileroffer_id) {
        map.set(item.selected_retaileroffer_id, item);
      }
    }
    return map;
  }, [activeListQuery.data]);

  // T12.1.8.7 — map the active list's "added-from" deal references → the item, so
  // an offer-less flyer card (no offer id) flips to "✓ On list" and Removes the
  // right item.
  const itemsByDealId = useMemo(() => {
    const map = new Map<string, ShoppingListItemWithDetails>();
    for (const item of activeListQuery.data?.items ?? []) {
      // Only ACTIVE (pending) items count as "on list" — see itemsByOfferId.
      if (item.status !== 'pending') continue;
      if (item.flyerdeal_id) {
        map.set(item.flyerdeal_id, item);
      }
    }
    return map;
  }, [activeListQuery.data]);

  // T12.5.1 (F) — the SystemProduct id(s) of every ACTIVE list item's product.
  // The ★ On your list BADGE is product-level: a deal lights it when its
  // SystemProduct id (offer id or flyer line id) is among these, so EVERY offer
  // for a product on the list shows the badge — not only the one offer the item
  // selected (which stays the "✓ On list" BUTTON signal, `cardOnList`). Built by
  // looking each pending item's `householdproduct_id` up in the products list
  // (which now carries `linked_systemproduct_ids`).
  const onListSystemProductIds = useMemo(() => {
    const productLinks = new Map<string, string[]>();
    for (const product of productsQuery.data?.products ?? []) {
      productLinks.set(product.id, product.linked_systemproduct_ids ?? []);
    }
    const set = new Set<string>();
    for (const item of activeListQuery.data?.items ?? []) {
      if (item.status !== 'pending') continue;
      const ids = productLinks.get(item.householdproduct_id);
      if (ids) for (const id of ids) set.add(id);
    }
    return set;
  }, [activeListQuery.data, productsQuery.data]);

  // ---- Filter / search state ------------------------------------------------
  const [query, setQuery] = useState('');
  const [rewriteOff, setRewriteOff] = useState(false);
  const setSearchQuery = useCallback((next: string) => {
    setRewriteOff(false);
    setQuery(next);
  }, []);
  const [debouncedTypeahead, setDebouncedTypeahead] = useState('');
  const [typeaheadOpen, setTypeaheadOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dismissTypeahead = useCallback(() => {
    setTypeaheadOpen(false);
    searchInputRef.current?.blur();
  }, []);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedTypeahead(query.trim()), 250);
    return () => clearTimeout(timer);
  }, [query]);
  const typeaheadQuery = useDealsSearchSuggestions(debouncedTypeahead);
  // T40 — the store filter is a multi-select SET of store ids; an EMPTY set is
  // the canonical "all stores" state (no sentinel string). The visibility
  // predicate is set-membership: `set.size === 0 || set.has(card.storeId)`.
  const [storeFilter, setStoreFilterRaw] = useState<Set<string>>(
    () => new Set<string>(),
  );

  // T39 — once the user changes OR clears the store filter, the GPS-derived
  // in-store default must never reapply (AC6). Every user-facing store-filter
  // change goes through `setStoreFilter`, which marks the filter touched; the
  // one-time default effect below uses the raw setter and is gated on this ref.
  const storeFilterTouchedRef = useRef(false);
  const setStoreFilter = useCallback((next: Set<string>) => {
    storeFilterTouchedRef.current = true;
    setStoreFilterRaw(next);
  }, []);

  // T39 (AC6 / AC7) — apply the in-store detected store as the filter default,
  // at most once per page visit. Ported to the multi-select set shape: the set
  // is initialized to the ONE detected store. Gated on:
  //   - a confident in-store result carrying a matched store id;
  //   - that store being one of the household's selected stores (the filter's
  //     id space — otherwise the filter would point at an absent store);
  //   - the user not having touched the filter yet (a late GPS fix that arrives
  //     after a manual change/clear must NOT override it).
  // A ref records that the default already ran, so a re-render (or a second fix)
  // never reapplies it.
  const storeDefaultAppliedRef = useRef(false);
  useEffect(() => {
    if (storeDefaultAppliedRef.current || storeFilterTouchedRef.current) return;
    if (locationContext.kind !== 'in-store' || !locationContext.matchedStoreId) {
      return;
    }
    if (!selectedStoreIds.includes(locationContext.matchedStoreId)) return;
    storeDefaultAppliedRef.current = true;
    // Use the RAW setter: this is the system default, not a user touch, so it
    // must not mark the filter touched (a later user clear still wins).
    setStoreFilterRaw(new Set([locationContext.matchedStoreId]));
  }, [locationContext, selectedStoreIds]);
  const [searchFocused, setSearchFocused] = useState(false);
  const [sheetKey, setSheetKey] = useState<string | null>(null);
  // T12.1.8.4 / T12.1.8.7 — the confirm-create prompt: set when an add returns the
  // "would create — confirm?" signal (no HouseholdProduct linked to the resolved
  // SystemProduct line yet). Re-issues the SAME add with `confirmCreate`. Carries
  // either the offer (offer-add) or the deal + chosen line (flyer-add).
  const [confirmCreate, setConfirmCreate] = useState<{
    productName: string;
    offerId?: string;
    flyerDealId?: string;
    systemProductId?: string;
  } | null>(null);
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);
  // T12.1.8.7 — the combo "ask which" prompt: set when a tapped deal covers
  // several lines. The household picks one; the add re-fires with that line.
  const [comboPick, setComboPick] = useState<{
    flyerDealId: string;
    title: string;
    lines: FlyerDealLine[];
  } | null>(null);

  // T55.3 round-2 — `query` IS the single bar's text (T55 invariant #1); the
  // server-side fit-ranked read (DealsSearch, controlled) owns everything
  // else once this is non-empty.
  const searching = query.trim().length > 0;

  // T55.3 round-3 (owner decision) — search results render as the SAME rich
  // deal cards the browse list uses. `DealsPage` (not `DealsSearch`) owns
  // `DealCardRow` + the add/remove/sheet-open handlers, so it reads the
  // search response itself (React Query dedupes this against `DealsSearch`'s
  // own `useDealsSearch(bar, undefined, rewrite)` call below — same args,
  // same cache key, ONE network request) to build the card list + wire the
  // sheet lookup. Structural call: lifted here rather than passed as
  // handlers into `DealsSearch`, so the existing `sheetKey`/`sheetEntry`/
  // `handleAdd`/`handleRemove` machinery needs no fork.
  const searchQueryForCards = useDealsSearch(
    query,
    undefined,
    rewriteOff ? 'off' : undefined,
  );
  const searchDeals: DealsSearchRow[] = searchQueryForCards.data?.deals ?? [];
  // Ranking = fit order (response order, server-side), same under every
  // sort; "For you" (default) breaks fit ties with `score`, computed by the
  // SAME `deriveTags`/`dealScore` the browse list uses below — no second
  // engine (T55.13 Boundaries).
  const searchCardsWithTags = useMemo(
    () => searchDeals.map((deal) => {
      const storeId = deal.store_location_ids?.[0] ?? '';
      const card = { ...searchRowToCard(deal), storeId, storeName: storeNameById.get(storeId) ?? '' };
      const tags = deriveTags(
        card,
        listTokens,
        productTokens,
        likedBrandNames,
        likedSystemProductIds,
        likedProductNames,
      );
      return { card, deal, tags, score: dealScore(tags, card) };
    }),
    [
      searchDeals,
      storeNameById,
      listTokens,
      productTokens,
      likedBrandNames,
      likedSystemProductIds,
      likedProductNames,
    ],
  );
  // T55.12/T55.14/T55.17 — the counting module's input rows for search: one
  // row per (already deal-deduplicated) search result, carrying exactly the
  // category/attribute/store evidence `dealTagPayload` put on the wire.
  // Categories are now weight-carrying (a `Map`, like `attributes`) — the
  // wire payload already carries a per-tag `weight` for `category_tags`
  // (same shape as `attribute_tags`), so the exclude arm's confidence floor
  // (`packages/shared/src/types/dealCounters.ts`) has real data to read.
  const searchDealCounterRows: DealCounterRow[] = useMemo(
    () =>
      searchDeals.map((deal) => ({
        id: deal.id,
        categories: new Map((deal.category_tags ?? []).map((tag) => [tag.name.toLowerCase(), tag.weight])),
        attributes: new Map((deal.attribute_tags ?? []).map((tag) => [tag.name.toLowerCase(), tag.weight])),
        storeIds: deal.store_location_ids ?? [],
      })),
    [searchDeals],
  );
  const searchDealCounters = useMemo(
    () =>
      computeDealCounters(searchDealCounterRows, {
        categories: selectedCategories,
        attributes: selectedAttributes,
        stores: storeFilter,
      }),
    [searchDealCounterRows, selectedCategories, selectedAttributes, storeFilter],
  );

  useEffect(() => {
    if (!searching || !searchQueryForCards.data) return;
    // T55.12/T55.14/T55.17 — both Categories and Attributes come from the
    // CLIENT's own card evidence (the module's exhaustive derivation), not
    // the server's `filter_options` (categories: excluded the query's own
    // word, the T55.11 bug; attributes: PER_SECTION_CAP=4, T55.14 — a
    // selection outside the capped 4 would have been wrongly dropped here).
    // A term counts as present at weight > 0, matching `computeDealCounters`'
    // own presence threshold (`presenceTerms`) — now identical for both tag
    // kinds (T55.17). This checks the WHOLE fresh result set (not the
    // current attribute/store selection's disjunctive base) — cleanup after
    // a query change asks "does this term exist at all in the new results",
    // not "does it survive today's other filters".
    const freshDeals = searchQueryForCards.data.deals;
    const categoryTerms = new Set(
      freshDeals.flatMap((deal) =>
        (deal.category_tags ?? []).filter((tag) => tag.weight > 0).map((tag) => tag.name.toLowerCase()),
      ),
    );
    const attributeTerms = new Set(
      freshDeals.flatMap((deal) =>
        (deal.attribute_tags ?? []).filter((tag) => tag.weight > 0).map((tag) => tag.name.toLowerCase()),
      ),
    );
    const dropped: string[] = [];
    setSelectedCategories((current) => {
      const next = new Map(Array.from(current).filter(([term]) => {
        const keep = categoryTerms.has(term);
        if (!keep) dropped.push(term);
        return keep;
      }));
      return next.size === current.size ? current : next;
    });
    setSelectedAttributes((current) => {
      const next = new Map(Array.from(current).filter(([term]) => {
        const keep = attributeTerms.has(term);
        if (!keep) dropped.push(term);
        return keep;
      }));
      return next.size === current.size ? current : next;
    });
    setClearedFilters(dropped);
  }, [query, searchQueryForCards.data, searching]);

  const filteredSearchCards = useMemo(() => {
    const rows = searchCardsWithTags.filter(({ deal }) => searchDealCounters.filteredRowIds.has(deal.id));
    return rows.sort((a, b) => {
      if (sort === 'price') return (a.card.salePrice ?? Number.POSITIVE_INFINITY) - (b.card.salePrice ?? Number.POSITIVE_INFINITY);
      if (sort === 'price-desc') return (b.card.salePrice ?? Number.NEGATIVE_INFINITY) - (a.card.salePrice ?? Number.NEGATIVE_INFINITY);
      if (sort === 'ending') return (a.card.endDate ?? '').localeCompare(b.card.endDate ?? '');
      return b.deal.fit - a.deal.fit || b.score - a.score; // 'foryou' — fit first, personal relevance tiebreak.
    });
  }, [searchCardsWithTags, searchDealCounters, sort]);
  const api = useApi();
  // T55.9 known-gap fix — "result_picked" capture: fired once when a search
  // result card is opened (the pick affordance T55.9 shipped without).
  // Fire-and-forget, same contract as the other search-event capture calls
  // in `DealsSearch.tsx`.
  const handleSearchResultOpen = useCallback(
    (card: DealCard) => {
      setSheetKey(card.key);
      const pickedSystemProductId = card.systemProductId ?? card.lines[0]?.system_product_id;
      if (pickedSystemProductId) {
        void api.FlyerDeals.reportResultPicked(query.trim(), pickedSystemProductId).catch(() => {
          /* capture failures never surface to the shopper */
        });
      }
    },
    [api, query],
  );

  // ---- Merge offers + flyer deals into one flat card list -------------------
  const cards = useMemo<DealCard[]>(() => {
    const out: DealCard[] = [];
    for (const store of flyerStores) {
      for (const deal of store.deals) {
        out.push(flyerDealToCard(deal, store));
      }
    }
    for (const storeId of selectedStoreIds) {
      const report = offerReports[storeId];
      if (!report) continue;
      const storeName = storeNameById.get(storeId) ?? storeId;
      for (const offer of report.offers) {
        out.push(offerToCard(offer, storeName));
      }
    }
    return out;
  }, [flyerStores, selectedStoreIds, offerReports, storeNameById]);

  const cardsWithTags = useMemo(
    () =>
      cards.map((card) => {
        const tags = deriveTags(
          card,
          listTokens,
          productTokens,
          likedBrandNames,
          likedSystemProductIds,
          likedProductNames,
        );
        return { card, tags, score: dealScore(tags, card) };
      }),
    [
      cards,
      listTokens,
      productTokens,
      likedBrandNames,
      likedSystemProductIds,
      likedProductNames,
    ],
  );

  // T12.1.8.7 / T27.2.7.3 — reopen the added deal: when `?deal=<flyerdeal_id>` is
  // present and a card for that deal is loaded, open its detail sheet.
  //
  // DL2 (T27.2.7.3): the param is the sheet's breadcrumb-in-URL and must SURVIVE
  // while the sheet is open. The old code immediately `replace`-stripped it, which
  // overwrote the `/deals?deal=X` history entry that a list-item "View deal" had
  // pushed — so the Android hardware Back (X1) could no longer return to the
  // originating list. Now the auto-open runs ONCE per deal id (guarded by a ref),
  // the param is left on the URL while open, and it is cleared only when the sheet
  // is dismissed (see the sheet's onClose below). So Back closes the sheet, then a
  // second Back does router-back to the originating list.
  const reopenDealId = searchParams.get('deal');
  const autoOpenedDealId = useRef<string | null>(null);
  useEffect(() => {
    if (!reopenDealId) {
      // No param — reset the guard so a fresh navigation with the same id later
      // can re-open the sheet.
      autoOpenedDealId.current = null;
      return;
    }
    // Auto-open at most once per deal id, so closing the sheet (which leaves the
    // param on the URL as the breadcrumb) does not immediately reopen it.
    if (autoOpenedDealId.current === reopenDealId) return;
    // Wait until the flyer data has loaded — `cards` is empty on the first
    // render, before the deal we want to reopen has arrived.
    if (flyerQuery.isLoading) return;
    const match = cards.find((c) => c.flyerDealId === reopenDealId);
    if (match) {
      autoOpenedDealId.current = reopenDealId;
      setSheetKey(match.key);
    } else {
      // A purged deal finds no card; mark it consumed and clear the dead param so
      // the list item falls back to the product + the current live deal.
      autoOpenedDealId.current = reopenDealId;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('deal');
          return next;
        },
        { replace: true },
      );
    }
  }, [reopenDealId, cards, flyerQuery.isLoading, setSearchParams]);

  // Closing the deal sheet clears the `?deal=` breadcrumb (without adding a
  // history entry) so the URL reflects the closed state and a re-render does not
  // reopen the sheet. The hardware-Back path closes the sheet via this same
  // handler (the deal sheet's Modal registers in the overlay stack, T27.2.7.3).
  const closeDealSheet = useCallback(() => {
    setSheetKey(null);
    if (searchParams.has('deal')) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('deal');
          return next;
        },
        { replace: true },
      );
    }
  }, [searchParams, setSearchParams]);

  // A card is "on list" (the ✓ On list BUTTON signal) when its exact anchor
  // matches a list item: an offer card by its offer id, an offer-less flyer card
  // by its "added-from" deal id. This means "this exact offer is the one you
  // added" — the button must Remove THAT item.
  const cardOnList = useCallback(
    (card: DealCard): boolean =>
      (!!card.offerId && itemsByOfferId.has(card.offerId)) ||
      (!card.offerId && !!card.flyerDealId && itemsByDealId.has(card.flyerDealId)),
    [itemsByOfferId, itemsByDealId],
  );

  // T12.5.1 (F) — the ★ On your list BADGE is PRODUCT-level: it lights whenever
  // the deal's product is on the list, regardless of which offer the item
  // selected. A product added without a chosen where-to-buy (the normal case)
  // selects no offer, so the offer-level `cardOnList` is false for every offer —
  // yet every offer for that product belongs on the list. Match the card's
  // SystemProduct identity (its resolved offer id OR any flyer line id) against
  // the active list's product SystemProduct ids; OR fall back to the exact-anchor
  // membership so a flyer-added offer-less deal still badges.
  const cardOnListBadge = useCallback(
    (card: DealCard): boolean => {
      if (cardOnList(card)) return true;
      if (onListSystemProductIds.size === 0) return false;
      if (card.systemProductId != null && onListSystemProductIds.has(card.systemProductId)) {
        return true;
      }
      return card.lines.some((l) => onListSystemProductIds.has(l.system_product_id));
    },
    [cardOnList, onListSystemProductIds],
  );

  // T12.5.1 (C) — the full stacked badge set for a card, in the locked order.
  // Threads the page-level partitioned signal sets + the product-level on-list
  // flag into the pure `deriveBadges`.
  const cardBadges = useCallback(
    (card: DealCard): DealBadge[] =>
      deriveBadges(card, { onList: cardOnListBadge(card), ...badgeSignals }),
    [cardOnListBadge, badgeSignals],
  );

  // T55.3 round-2 — the client-side title/brand text filter retired (the
  // single bar now drives the server-side fit-ranked read exclusively via
  // DealsSearch; see the `searching` branch in the render below). This list
  // is only ever rendered while the bar is EMPTY — no query branch; membership
  // narrows only by store/category/attribute (T55.13: browse applies no
  // personalization filter — see `visibleCards`).
  const browsePayloadByCardKey = useMemo(() => {
    // T55.17 — categories carry weight now too, the same shape as
    // attributes (the wire payload already carries a per-tag `weight` for
    // `category_tags`).
    const map = new Map<string, { categories: Map<string, number>; attributes: Map<string, number> }>();
    for (const store of flyerStores) {
      for (const deal of store.deals) {
        map.set(`flyer-${store.storelocation_id}-${deal.id}`, {
          categories: new Map((deal.category_tags ?? []).map((tag) => [tag.name.toLowerCase(), tag.weight])),
          attributes: new Map((deal.attribute_tags ?? []).map((tag) => [tag.name.toLowerCase(), tag.weight])),
        });
      }
    }
    return map;
  }, [flyerStores]);

  // T55.12 — both the counting module's rows and the rendered card list
  // derive from this SAME set, so a facet's option count and the resulting
  // "Show N deals"/heading/rendered-card total can never drift apart the
  // way the old server-computed `filter_options` could. T55.13 retired the
  // For-you|All-deals scope predicate that used to narrow this set FIRST —
  // `dealCounterRows` now maps every card, so the module picks up the
  // widened browse membership with no rework of its own.
  const dealCounterRows: DealCounterRow[] = useMemo(
    () =>
      cardsWithTags.map(({ card }) => {
        const payload = browsePayloadByCardKey.get(card.key);
        return {
          id: card.key,
          categories: payload?.categories ?? EMPTY_TAG_MAP,
          attributes: payload?.attributes ?? EMPTY_TAG_MAP,
          storeIds: [card.storeId],
        };
      }),
    [cardsWithTags, browsePayloadByCardKey],
  );
  const dealCounters = useMemo(
    () =>
      computeDealCounters(dealCounterRows, {
        categories: selectedCategories,
        attributes: selectedAttributes,
        stores: storeFilter,
      }),
    [dealCounterRows, selectedCategories, selectedAttributes, storeFilter],
  );

  // T55.13 — browse renders every eligible deal at the selected stores; the
  // retired For-you|All-deals scope predicate no longer narrows membership.
  // Personalization (tags.onList / tags.tracked, folded into `score` via
  // `dealScore`) drives ordering ONLY now, under the "For you" sort.
  const visibleCards = useMemo(() => {
    return cardsWithTags
      .filter(({ card }) => dealCounters.filteredRowIds.has(card.key))
      .sort((a, b) => {
        if (sort === 'price') return (a.card.salePrice ?? a.card.regularPrice ?? Number.POSITIVE_INFINITY) - (b.card.salePrice ?? b.card.regularPrice ?? Number.POSITIVE_INFINITY);
        if (sort === 'price-desc') return (b.card.salePrice ?? b.card.regularPrice ?? Number.NEGATIVE_INFINITY) - (a.card.salePrice ?? a.card.regularPrice ?? Number.NEGATIVE_INFINITY);
        if (sort === 'ending') return (a.card.endDate ?? '9999').localeCompare(b.card.endDate ?? '9999');
        return b.score - a.score; // 'foryou' (default) — personal relevance first.
      });
  }, [cardsWithTags, dealCounters, sort]);

  const incrementalListResetKey = useMemo(
    () => JSON.stringify({
      sort,
      categories: Array.from(selectedCategories),
      attributes: Array.from(selectedAttributes),
      stores: Array.from(storeFilter),
      query,
    }),
    [query, selectedAttributes, selectedCategories, sort, storeFilter],
  );
  const browseIncrementalList = useIncrementalList(visibleCards, { resetKey: incrementalListResetKey });
  const searchIncrementalList = useIncrementalList(filteredSearchCards, { resetKey: incrementalListResetKey });

  // ---- Add / remove handlers (D5/D6) ----------------------------------------
  // T12.1.8.4 — an offer-only add can come back as a "would create — confirm?"
  // signal when the household has no product linked to the offer's line yet. We
  // surface a one-tap confirm rather than silently growing the catalog.
  //
  // T12.1.8.7 — the add is SystemProduct-first: any deal with a SystemProduct
  // anchor is addable, offer or no offer.
  //   - an offer card sends `selectedRetailerOfferId` (unchanged);
  //   - an offer-less flyer card sends `flyerDealId` (+ the chosen line for a
  //     combo). A combo (several lines) first prompts the household to pick one;
  //     a single-line deal adds straight away.

  // Fire a flyer-add for a deal + an optional chosen line. Surfaces the
  // confirm-create prompt (carrying the deal + line) when the resolved line has
  // no linked HouseholdProduct yet.
  const addFlyerDeal = useCallback(
    (flyerDealId: string, systemProductId?: string) => {
      // Omit `systemProductId` when absent — `exactOptionalPropertyTypes` rejects
      // an explicit `undefined` on an optional field.
      const request: AddShoppingListItemRequest = {
        flyerDealId,
        quantity: 1,
        ...(systemProductId ? { systemProductId } : {}),
      };
      void addToList
        .mutateAsync(request)
        .then((result) => {
          if (result && 'needsConfirmation' in result) {
            setConfirmCreate({
              flyerDealId,
              productName: result.systemProductName,
              ...(systemProductId ? { systemProductId } : {}),
            });
          }
        })
        .catch(() => {
          /* mutation error surfaces via addToList.isError; no extra handling */
        });
    },
    [addToList],
  );

  const handleAdd = useCallback(
    (card: DealCard) => {
      if (card.offerId) {
        const offerId = card.offerId;
        void addToList
          .mutateAsync({ selectedRetailerOfferId: offerId, quantity: 1 })
          .then((result) => {
            if (result && 'needsConfirmation' in result) {
              setConfirmCreate({
                offerId,
                productName: result.systemProductName,
              });
            }
          })
          .catch(() => {
            /* mutation error surfaces via addToList.isError; no extra handling */
          });
        return;
      }
      // Offer-less flyer deal — add via its SystemProduct line(s).
      if (!card.flyerDealId || card.lines.length === 0) return;
      if (card.lines.length > 1) {
        // Combo deal — ask which line before adding (assistive, not authoritative).
        setComboPick({
          flyerDealId: card.flyerDealId,
          title: card.title,
          lines: card.lines,
        });
        return;
      }
      addFlyerDeal(card.flyerDealId);
    },
    [addToList, addFlyerDeal],
  );

  // The household picked a line from a combo deal → add that line's product.
  const handleComboPick = useCallback(
    (line: FlyerDealLine) => {
      if (!comboPick) return;
      addFlyerDeal(comboPick.flyerDealId, line.system_product_id);
      setComboPick(null);
    },
    [comboPick, addFlyerDeal],
  );

  // Confirm the create — re-issue the SAME add with `confirmCreate: true`. The
  // offer-add re-sends the offer; the flyer-add re-sends the deal + chosen line.
  const handleConfirmCreate = useCallback(() => {
    if (!confirmCreate) return;
    setConfirmSubmitting(true);
    // Re-send exactly the anchor the original add carried — omit the absent ones
    // (`exactOptionalPropertyTypes` rejects explicit `undefined`).
    const request: AddShoppingListItemRequest = {
      quantity: 1,
      confirmCreate: true,
      ...(confirmCreate.offerId
        ? { selectedRetailerOfferId: confirmCreate.offerId }
        : {}),
      ...(confirmCreate.flyerDealId
        ? { flyerDealId: confirmCreate.flyerDealId }
        : {}),
      ...(confirmCreate.systemProductId
        ? { systemProductId: confirmCreate.systemProductId }
        : {}),
    };
    void addToList
      .mutateAsync(request)
      .then(() => {
        setConfirmCreate(null);
      })
      .catch(() => {
        /* error surfaces via addToList.isError */
      })
      .finally(() => {
        setConfirmSubmitting(false);
      });
  }, [addToList, confirmCreate]);

  const handleRemove = useCallback(
    (card: DealCard) => {
      if (!activeListId) return;
      // Match the SLI by the card's anchor: an offer card by its offer id, an
      // offer-less flyer card by its "added-from" deal id.
      const item = card.offerId
        ? itemsByOfferId.get(card.offerId)
        : card.flyerDealId
          ? itemsByDealId.get(card.flyerDealId)
          : undefined;
      if (!item) return;
      deleteItem.mutate({ listId: activeListId, itemId: item.id });
    },
    [deleteItem, activeListId, itemsByOfferId, itemsByDealId],
  );

  // ---- Loading / gating states (preserve both old pages' behaviour) ---------
  const storesGateLoading =
    selectedStoresQuery.isLoading || storesQuery.isLoading;

  const offersStillLoading = selectedStoreIds.some(
    (id) => offerReports[id]?.isLoading ?? true,
  );
  const anyOfferSyncing = selectedStoreIds.some(
    (id) => offerReports[id]?.syncing ?? false,
  );

  if (storesGateLoading) {
    return (
      <div style={emptyStyle} data-testid="deals-stores-loading">
        <Spinner />
      </div>
    );
  }

  if (selectedStoreIds.length === 0) {
    return (
      <div style={pageStyle} data-testid="deals-page">
        <Card padding="lg" data-testid="deals-no-stores">
          <div style={emptyStyle}>
            <p>No stores selected for this household.</p>
            <p>Go to Settings › Stores to pick the ones you shop at.</p>
          </div>
        </Card>
      </div>
    );
  }

  const flyerLoading = flyerQuery.isLoading;
  const flyerError = flyerQuery.isError;
  const nothingYet = cards.length === 0;

  const inColdStart =
    nothingYet &&
    !flyerError &&
    (anyOfferSyncing || (flyerColdActive && !hasAnyFlyerDeal));

  // T55.3 round-3 — a sheet opened from a search result card resolves via
  // `searchCardsWithTags` (search cards carry no `tags`/`score`, only the
  // card itself, so `DealDetailSheet`'s own props — which need `card`, not
  // the browse-list's tags — are unaffected either way).
  const sheetEntry = sheetKey
    ? (cardsWithTags.find(({ card }) => card.key === sheetKey) ??
        searchCardsWithTags.find(({ card }) => card.key === sheetKey) ??
        null)
    : null;

  // T40 — the store dropdown lists ONLY the household's selected stores (the
  // visibility gate), in selection order, with their resolved names.
  const storesForMenu = selectedStoreIds.map((id) => ({
    id,
    name: storeNameById.get(id) ?? id,
  }));
  // T55.12/T55.14 — Category AND Attribute options are both exhaustive +
  // disjunctive, from the SAME counting module that produces the deal count
  // (browse: `dealCounters`, search: `searchDealCounters`) — no
  // already-in-bar exclusion, no server-side PER_SECTION_CAP=4
  // (`filter_options.attributes`, searchRefinementChips.ts — left untouched
  // on the backend and no longer consumed here), no SystemProduct-keyed
  // server counts. Tri-state cycling, the homogeneous info row, and the 0.5
  // exclude floor are unaffected — only the OPTION LIST source moved.
  // Store option counts are ALSO module-derived (disjunctive: the OTHER
  // sections applied, the store's own selection omitted) — the store LIST
  // itself stays the fixed `storesForMenu` set (every selected store is
  // always a valid filter option), only its per-row count changes source.
  const activeDealCounters = searching ? searchDealCounters : dealCounters;
  const categoryOptionsForDrawer: DealsSearchFilterOption[] = activeDealCounters.categoryOptions.map(
    (option) => ({ term: option.term, count: option.count, defaultIntent: 'require', group: 'category' }),
  );
  const attributeOptionsForDrawer: DealsSearchFilterOption[] = activeDealCounters.attributeOptions.map(
    (option) => ({ term: option.term, count: option.count, defaultIntent: 'require', group: 'attribute' }),
  );
  const storeCountById = new Map(activeDealCounters.storeOptions.map((option) => [option.term, option.count]));
  const storeOptions = storesForMenu.map((store) => ({
    ...store,
    count: storeCountById.get(store.id) ?? 0,
  }));
  const activeFilterCount = selectedCategories.size + selectedAttributes.size + storeFilter.size;
  const shownCount = searching ? filteredSearchCards.length : visibleCards.length;
  const clearAllFilters = () => {
    setSelectedCategories(new Map());
    setSelectedAttributes(new Map());
    setStoreFilter(new Set());
  };

  return (
    <div
      style={pageStyle}
      data-testid="deals-page"
      onWheelCapture={() => {
        if (typeaheadOpen) dismissTypeahead();
      }}
      onTouchMoveCapture={() => {
        if (typeaheadOpen) dismissTypeahead();
      }}
    >
      {/* No in-page title — the app bar already renders "Deals" (T39 / AC1),
          consistent with the Feedback tab. */}

      {/* Per-store offer loaders — render nothing, just feed the merge. */}
      {selectedStoreIds.map((storeId) => (
        <StoreLocationOffersLoader
          key={storeId}
          storeId={storeId}
          onReport={handleReport}
        />
      ))}

      {/* T40 — the search field + the filter bar pin together as ONE sticky
          cluster below the app header (`--app-header-height`, published by
          AppShell), so filtering stays reachable while browsing a long list. */}
      <div style={stickyClusterStyle} data-testid="deals-sticky">
        {/* Search. Typing drives the ranked search read; the personalized
            typeahead below is the bar's only dropdown. */}
        <div
          style={searchFocused ? searchBarFocusedStyle : searchBarStyle}
          data-testid="deals-search"
        >
          <span style={searchIconStyle} aria-hidden="true">
            🔎
          </span>
          <input
            ref={searchInputRef}
            style={searchInputStyle}
            value={query}
            placeholder="Search a product… e.g. eggs"
            autoComplete="off"
            aria-label="Search deals"
            onChange={(e) => { setSearchQuery(e.target.value); setTypeaheadOpen(true); }}
            onFocus={() => { setSearchFocused(true); setTypeaheadOpen(true); }}
            onBlur={() => { setSearchFocused(false); setTypeaheadOpen(false); }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                dismissTypeahead();
              }
            }}
            data-testid="deals-search-input"
          />
          {searching && (
            <button
              type="button"
              style={searchClearStyle}
              aria-label="Clear search"
              onClick={() => setSearchQuery('')}
              data-testid="deals-search-clear"
            >
              ✕
            </button>
          )}
          {typeaheadOpen && (typeaheadQuery.data?.suggestions.length ?? 0) > 0 && (
            <div style={typeaheadStyle} role="listbox" aria-label="Search suggestions" data-testid="deals-typeahead">
              {typeaheadQuery.data?.suggestions.map((suggestion) => (
                <button
                  key={`${suggestion.source}-${suggestion.query}`}
                  type="button"
                  role="option"
                  style={typeaheadRowStyle}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => { setSearchQuery(suggestion.query); setTypeaheadOpen(false); }}
                  data-testid={`deals-typeahead-${suggestion.query}`}
                >
                  <span>{suggestion.query}</span>
                  <span style={typeaheadSourceStyle}>{suggestion.source}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Permanent Filters + sort row — identical in browse and search
            (T55.13 retired the scope segment; browse always renders every
            eligible deal, "For you" sort ranks personal relevance first). */}
        <div style={filterBarStyle} data-testid="deals-filters">
          <button
            type="button"
            style={activeFilterCount > 0 ? filterButtonActiveStyle : filterButtonStyle}
            onClick={() => setFiltersOpen(true)}
            data-testid="deals-filters-open"
          >
            Filters
          </button>
          <select
            style={sortSelectStyle}
            value={sort}
            onChange={(event) => setSort(event.target.value as SortMode)}
            aria-label="Sort deals"
            data-testid="deals-sort"
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        <ActiveFilterChips
          categories={selectedCategories}
          attributes={selectedAttributes}
          stores={storesForMenu}
          selectedStores={storeFilter}
          onRemoveCategory={(term) => setSelectedCategories((current) => { const next = new Map(current); next.delete(term); return next; })}
          onRemoveAttribute={(term) => setSelectedAttributes((current) => { const next = new Map(current); next.delete(term); return next; })}
          onRemoveStore={(id) => setStoreFilter(new Set(Array.from(storeFilter).filter((storeId) => storeId !== id)))}
          onClear={clearAllFilters}
        />
        {clearedFilters.length > 0 && <div style={lifecycleStyle} role="status" data-testid="deals-filters-cleared">Cleared unavailable filters: {clearedFilters.join(', ')}</div>}
      </div>

      {/* T12.6.1 — the located-but-no-GPS inline ask: shown only for a household
          that has a home-coords fallback (store discovery/deals still work)
          but whose device GPS itself isn't working. Dismissible, 14-day
          re-surface (dismissal.ts); the enable action reuses AppShell's sole
          `useModeDetection` instance via the published location signal — no
          second `getCurrentPosition` call (T12.6 D5 single-caller invariant). */}
      {showLocationAsk && (
        <Card
          padding="md"
          style={{ marginBottom: blockGap }}
          data-testid="deals-location-ask"
        >
          <div style={locationAskRowStyle}>
            <span style={locationAskTextStyle}>
              Turn on location to auto-show deals for the store you&apos;re in.
            </span>
            <div style={locationAskActionsStyle}>
              <button
                type="button"
                style={locationAskEnableStyle}
                onClick={requestDetection}
                data-testid="deals-location-ask-enable"
              >
                Turn on
              </button>
              <button
                type="button"
                style={locationAskDismissStyle}
                onClick={dismissLocationAsk}
                aria-label="Dismiss"
                data-testid="deals-location-ask-dismiss"
              >
                ✕
              </button>
            </div>
          </div>
        </Card>
      )}

      <DealsFiltersDrawer
        open={filtersOpen}
        resultCount={shownCount}
        optionSetCount={searching ? searchDeals.length : cards.length}
        categories={categoryOptionsForDrawer}
        attributes={attributeOptionsForDrawer}
        stores={storeOptions}
        selectedCategories={selectedCategories}
        selectedAttributes={selectedAttributes}
        selectedStores={storeFilter}
        onCategoriesChange={setSelectedCategories}
        onAttributesChange={setSelectedAttributes}
        onStoresChange={setStoreFilter}
        onClose={() => setFiltersOpen(false)}
      />

      {/* T55.3 round-2 (owner decision, 2026-07-10) — ONE search on /deals.
          The sticky-cluster field above is the single bar; while it carries
          text, DealsSearch owns the results (server-side fit-ranked read,
          correction+undo, honest empty state, miss-report) and the
          T40 client-filtered card list + its heading/count all stay hidden —
          DealsSearch IS the results while searching. `query` and exact-mode
          state are lifted here, so both search hooks always share one cache
          key. The T40 filter drawer narrows the returned payload locally. */}
      {searching ? (
        <DealsSearch
          query={query}
          exact={rewriteOff}
          onUndo={() => setRewriteOff(true)}
          resultCount={filteredSearchCards.length}
          renderResults={() => (
            <div data-testid="deals-search-results">
              {searchIncrementalList.drawnItems.map(({ card }, index) => (
                <DealCardRow
                  key={card.key}
                  lookAheadRef={index === searchIncrementalList.lookAheadIndex ? searchIncrementalList.lookAheadRef : undefined}
                  card={card}
                  badges={cardBadges(card)}
                  onList={cardOnList(card)}
                  onOpen={() => handleSearchResultOpen(card)}
                  onAdd={() => handleAdd(card)}
                  onRemove={() => handleRemove(card)}
                />
              ))}
            </div>
          )}
        />
      ) : (
        <>
          {/* 3 — Result heading (appends the deal count, AC9) */}
          <div style={headingStyle} data-testid="deals-heading">
            <ResultHeading
              searching={searching}
              query={query}
              storeFilter={storeFilter}
              storeNameById={storeNameById}
            />
            {' · '}
            <b style={headingEmphStyle} data-testid="deals-count">
              {visibleCards.length}
            </b>{' '}
            {visibleCards.length === 1 ? 'deal' : 'deals'}
          </div>

          {flyerError && (
            <Card padding="md" data-testid="deals-flyer-error">
              <p style={errorStyle}>
                Could not load flyer deals
                {flyerQuery.error instanceof Error
                  ? `: ${flyerQuery.error.message}`
                  : '.'}
              </p>
            </Card>
          )}

          {/* 6 — Results */}
          {(offersStillLoading || flyerLoading) && nothingYet && !inColdStart ? (
            <div style={emptyStyle} data-testid="deals-loading">
              <Spinner />
            </div>
          ) : inColdStart ? (
            <Card padding="lg" data-testid="deals-cold-start">
              <div style={coldStartStyle}>
                <Spinner />
                <span>Finding deals in your area…</span>
              </div>
            </Card>
          ) : visibleCards.length === 0 ? (
            <Card padding="lg" data-testid="deals-empty">
              <div style={emptyStyle}>
                {nothingYet
                  ? 'No deals or offers yet at your selected stores.'
                  : 'No deals match these filters.'}
              </div>
            </Card>
          ) : (
            <div data-testid="deals-list">
              {browseIncrementalList.drawnItems.map(({ card }, index) => (
                <DealCardRow
                  key={card.key}
                  lookAheadRef={index === browseIncrementalList.lookAheadIndex ? browseIncrementalList.lookAheadRef : undefined}
                  card={card}
                  badges={cardBadges(card)}
                  onList={cardOnList(card)}
                  onOpen={() => setSheetKey(card.key)}
                  onAdd={() => handleAdd(card)}
                  onRemove={() => handleRemove(card)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* 7 — Detail sheet */}
      {sheetEntry && (
        <DealDetailSheet
          card={sheetEntry.card}
          badges={cardBadges(sheetEntry.card)}
          allCards={cardsWithTags.map((e) => e.card)}
          selectedStoreIds={selectedStoreIds}
          onList={cardOnList(sheetEntry.card)}
          onClose={closeDealSheet}
          onAdd={() => handleAdd(sheetEntry.card)}
          onRemove={() => handleRemove(sheetEntry.card)}
        />
      )}

      {/* T12.1.8.7 — combo "ask which" prompt: a deal covering several lines */}
      {comboPick && (
        <ComboPickSheet
          title={comboPick.title}
          lines={comboPick.lines}
          onPick={handleComboPick}
          onClose={() => setComboPick(null)}
        />
      )}

      {/* T12.1.8.4 — confirm-create prompt (assistive, not silent) */}
      {confirmCreate && (
        <ConfirmCreateSheet
          productName={confirmCreate.productName}
          submitting={confirmSubmitting}
          onConfirm={handleConfirmCreate}
          onClose={() => setConfirmCreate(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Confirm-create sheet (T12.1.8.4) — a one-tap "add this product to your
// products and list?" when the offer's line isn't a product the household has
// yet. Bottom-sheet variant, mobile-first (≤390px shell).
// ---------------------------------------------------------------------------
const confirmBodyStyle: CSSProperties = {
  padding: `var(--space-4) var(--space-4) var(--space-6)`,
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-3)',
};

const confirmTitleStyle: CSSProperties = {
  fontSize: 'var(--font-size-md)',
  fontWeight: 'var(--font-weight-bold)',
  color: 'var(--color-text-primary)',
  margin: 0,
};

const confirmBodyTextStyle: CSSProperties = {
  fontSize: 'var(--font-size-2sm)',
  color: 'var(--color-text-secondary)',
  margin: 0,
};

function ConfirmCreateSheet({
  productName,
  submitting,
  onConfirm,
  onClose,
}: {
  productName: string;
  submitting: boolean;
  onConfirm: () => void;
  onClose: () => void;
}): JSX.Element {
  return (
    <Modal
      open
      onClose={onClose}
      variant="sheet"
      ariaLabel={`Add ${productName} to your products?`}
      hideCloseButton={false}
    >
      <div style={confirmBodyStyle} data-testid="deal-confirm-create">
        <h3 style={confirmTitleStyle}>Add to your products?</h3>
        <p style={confirmBodyTextStyle}>
          You don’t have <b>{productName}</b> in your products yet. Add it to your
          products and list?
        </p>
        <div style={sheetActionsStyle}>
          <button
            type="button"
            style={submitting ? sheetAddDisabledStyle : sheetAddStyle}
            disabled={submitting}
            onClick={onConfirm}
            data-testid="deal-confirm-create-confirm"
          >
            {submitting ? 'Adding…' : `Add ${productName}`}
          </button>
          <button
            type="button"
            style={sheetRemoveStyle}
            onClick={onClose}
            data-testid="deal-confirm-create-cancel"
          >
            Not now
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Combo pick sheet (T12.1.8.7) — a deal that covers several product lines is a
// "combo" ("Blackberries or Strawberries?"); the household picks which one to
// add. A single-line deal never reaches here (it adds straight away). Assistive,
// not authoritative — bottom-sheet variant, mobile-first (≤390px shell).
// ---------------------------------------------------------------------------
const comboLineStyle: CSSProperties = {
  width: '100%',
  minHeight: TOUCH,
  padding: 'var(--space-3)',
  borderRadius: 'var(--radius-md)',
  borderWidth: HAIRLINE,
  borderStyle: 'solid',
  borderColor: 'var(--color-border-strong)',
  backgroundColor: 'var(--color-bg-primary)',
  color: 'var(--color-text-primary)',
  fontSize: 'var(--font-size-base15)',
  fontWeight: 'var(--font-weight-semibold)',
  cursor: 'pointer',
  textAlign: 'left',
};

function ComboPickSheet({
  title,
  lines,
  onPick,
  onClose,
}: {
  title: string;
  lines: FlyerDealLine[];
  onPick: (line: FlyerDealLine) => void;
  onClose: () => void;
}): JSX.Element {
  return (
    <Modal
      open
      onClose={onClose}
      variant="sheet"
      ariaLabel={`Which product from ${title}?`}
      hideCloseButton={false}
    >
      <div style={confirmBodyStyle} data-testid="deal-combo-pick">
        <h3 style={confirmTitleStyle}>Which one?</h3>
        <p style={confirmBodyTextStyle}>
          This deal covers a few products. Pick the one to add to your list.
        </p>
        {lines.map((line) => (
          <button
            type="button"
            key={line.system_product_id}
            style={comboLineStyle}
            onClick={() => onPick(line)}
            data-testid={`deal-combo-pick-${line.system_product_id}`}
          >
            {line.name}
          </button>
        ))}
      </div>
    </Modal>
  );
}

function ResultHeading({
  searching,
  query,
  storeFilter,
  storeNameById,
}: {
  searching: boolean;
  query: string;
  storeFilter: ReadonlySet<string>;
  storeNameById: Map<string, string>;
}): JSX.Element {
  if (searching) {
    return (
      <>
        Results for <b style={headingEmphStyle}>“{query.trim()}”</b> — across all
        your stores
      </>
    );
  }
  // T40 — the store filter is a SET. One store → name it; several → "N stores".
  if (storeFilter.size === 1) {
    const id = Array.from(storeFilter)[0]!;
    return (
      <>
        On offer at <b style={headingEmphStyle}>{storeNameById.get(id) ?? id}</b>
      </>
    );
  }
  if (storeFilter.size > 1) {
    return (
      <>
        On offer at <b style={headingEmphStyle}>{storeFilter.size} stores</b>
      </>
    );
  }
  // T55.13 — browse always renders every eligible deal at the selected
  // stores; "For you" is a sort order now, not a scope, so the heading no
  // longer branches on it (prototype v-14).
  return (
    <>
      <b style={headingEmphStyle}>All deals</b> at your selected stores
    </>
  );
}

function DealCardRow({
  card,
  badges,
  onList,
  onOpen,
  onAdd,
  onRemove,
  lookAheadRef,
}: {
  card: DealCard;
  // T12.5.1 (C) — the full stacked badge set, already in the locked order.
  badges: DealBadge[];
  // Offer-level: drives the ✓ On list / + Add button (Remove the exact item).
  onList: boolean;
  onOpen: () => void;
  onAdd: () => void;
  onRemove: () => void;
  lookAheadRef?: Ref<HTMLDivElement> | undefined;
}): JSX.Element {
  const testId = `deals-item-${card.key}`;
  const onSale = card.salePrice != null;
  // T12.1.8.7 — addable whenever the card has a SystemProduct anchor: an offer
  // card (offer id) OR an offer-less flyer deal with ≥1 product line. Only a
  // flyer deal that resolved to NO line stays non-addable.
  const noAnchor = card.offerId == null && card.lines.length === 0;

  const addButton = (() => {
    if (noAnchor) {
      return (
        <button
          type="button"
          style={addBtnDisabledStyle}
          disabled
          title="No product to anchor this to yet."
          onClick={(e) => e.stopPropagation()}
          data-testid={`${testId}-add`}
        >
          + Add to list
        </button>
      );
    }
    if (onList) {
      return (
        <button
          type="button"
          style={addBtnOnListStyle}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          data-testid={`${testId}-add`}
        >
          ✓ On list
        </button>
      );
    }
    return (
      <button
        type="button"
        style={addBtnRestingStyle}
        onClick={(e) => {
          e.stopPropagation();
          onAdd();
        }}
        data-testid={`${testId}-add`}
      >
        + Add to list
      </button>
    );
  })();

  return (
    <div
      ref={lookAheadRef}
      role="button"
      tabIndex={0}
      style={dealCardStyle}
      onClick={onOpen}
      onKeyDown={(e) => {
        // Enter / Space activate a native button; replicate that for the
        // div-role-button. preventDefault on Space stops the page scrolling.
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          onOpen();
        }
      }}
      aria-label={card.title}
      data-testid={testId}
    >
      {card.imageUrl ? (
        <img src={card.imageUrl} alt="" style={dealImageStyle} />
      ) : (
        <span style={dealImageStyle} aria-hidden="true">
          🏷️
        </span>
      )}

      <div style={dealInfoStyle}>
        <div style={dealNameStyle}>
          {card.title}
          {onSale && (
            <span style={saleBadgeStyle} data-testid={`${testId}-sale`}>
              SALE
            </span>
          )}
        </div>
        <div style={dealSubStyle} data-testid={`${testId}-sub`}>
          {card.storeName}
          {onSale || card.isFunded
            ? ` · flyer${card.endDate ? ` · ${formatEndDate(card.endDate)}` : ''}`
            : ' · regular price'}
        </div>
        {badges.length > 0 && (
          <div style={badgeRowStyle}>
            {badges.map((b) => (
              <span
                key={b.kind}
                style={badgeStyleFor(b.tone)}
                data-testid={`${testId}-tag-${b.testidSuffix}`}
              >
                {b.label}
              </span>
            ))}
          </div>
        )}
      </div>

      <div style={dealActStyle}>
        <div style={priceColStyle}>
          {onSale ? (
            <>
              <div style={nowSaleStyle} data-testid={`${testId}-now`}>
                {formatPrice(card.salePrice as number, card.priceCurrency)}
                {priceBasisSuffix(card.priceBasis)}
              </div>
              {card.regularPrice != null &&
                card.regularPrice > (card.salePrice as number) && (
                  <div style={wasStyle} data-testid={`${testId}-was`}>
                    {formatPrice(card.regularPrice, card.priceCurrency)}
                  </div>
                )}
            </>
          ) : card.regularPrice != null ? (
            <div style={nowRegularStyle} data-testid={`${testId}-regular`}>
              {formatPrice(card.regularPrice, card.priceCurrency)}
            </div>
          ) : null}
        </div>
        {addButton}
      </div>
    </div>
  );
}
